/**
 * S5 · Runs. The queue, the workers, and the failures with their reason.
 *
 * Two things here are buttons: a failed run can be retried, and the page
 * refreshes itself so a queue that is draining looks like it is draining.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  Button,
  Card,
  Empty,
  LinkButton,
  PageHead,
  Pill,
  Problem,
  Stat,
  StatGrid,
  Table,
  Td,
  Th,
  Toolbar,
  Stack,
} from "@/components/kit";
import { ago, loadRuns, retryJob, type Runs } from "@/lib/statsEngine/api";
import { useApi } from "@/spa/useApi";

const REFRESH_MS = 5000;

/** A job's state, as a colour anyone can name. */
const STATUS_TONE: Record<string, "good" | "warn" | "accent" | "bad" | "neutral"> = {
  succeeded: "good",
  doing: "accent",
  todo: "warn",
  failed: "bad",
};

export function StatsRunsPage() {
  const { sport = "" } = useParams<{ sport: string }>();
  const load = useCallback(() => loadRuns(), []);
  const { data, error, reload } = useApi<Runs>(load, [], "the admin API is not reachable");
  const [said, setSaid] = useState("");

  useEffect(() => {
    const id = setInterval(reload, REFRESH_MS);
    return () => clearInterval(id);
  }, [reload]);

  const retry = useCallback(
    async (id: number) => {
      setSaid("");
      try {
        await retryJob(id);
        setSaid(`Run ${id} queued again.`);
        reload();
      } catch (cause) {
        setSaid(cause instanceof Error ? cause.message : "could not retry");
      }
    },
    [reload],
  );

  const waiting = data ? Object.values(data.depth).reduce((n, byStatus) => n + (byStatus.todo ?? 0), 0) : 0;
  const running = data ? Object.values(data.depth).reduce((n, byStatus) => n + (byStatus.doing ?? 0), 0) : 0;

  if (error) return <Problem>Could not reach the admin service: {error}</Problem>;

  return (
    <Stack>
      <PageHead
        title="Runs"
        note="Every stat is worked out by a job on a queue — a match finished, a schedule fired, or somebody pressed Run. This is that queue, and whether anything is draining it."
        actions={
          <Toolbar>
            <LinkButton to={`/${sport}/stats-engine`}>Stats</LinkButton>
            <LinkButton to={`/${sport}/stats-engine/catalogue`}>Catalogue</LinkButton>
          </Toolbar>
        }
      />

      {said ? <Card>{said}</Card> : null}

      {data ? (
        <>
          <StatGrid>
            <Stat label="Waiting" value={waiting} note={waiting ? "queued, not started" : "queue is empty"} />
            <Stat label="Running" value={running} />
            <Stat
              label="Oldest wait"
              value={data.oldestWaitingSeconds === null ? "—" : `${data.oldestWaitingSeconds}s`}
              note="how long the front of the queue has sat"
            />
            <Stat
              label="Workers alive"
              value={data.workersAlive}
              note={data.workersAlive ? "draining the queue" : "nothing is draining it"}
            />
            <Stat
              label="Failed"
              value={data.recentFailures}
              note="in the last 24 hours"
            />
          </StatGrid>

          {data.jobs.length === 0 ? (
            <Card>
              <Empty
                title="Nothing has run yet"
                note="A run starts when a match finishes, a schedule fires, or somebody presses Run on the Stats tab."
              />
            </Card>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>#</Th>
                  <Th>Lane</Th>
                  <Th>Stat</Th>
                  <Th>Scope</Th>
                  <Th>Status</Th>
                  <Th num>Tries</Th>
                  <Th>Queued</Th>
                  <Th>Ended</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {data.jobs.map((job) => (
                  <tr key={job.id}>
                    <Td quiet>{job.id}</Td>
                    <Td>{job.lane}</Td>
                    <Td strong>{job.key}</Td>
                    <Td quiet>
                      {job.scopeKind}:{job.scopeRef.slice(0, 8)}
                    </Td>
                    <Td>
                      <Pill tone={STATUS_TONE[job.status] ?? "neutral"} dot>
                        {job.status}
                      </Pill>
                      {job.error ? <div className="muted">{job.error}</div> : null}
                    </Td>
                    <Td num>{job.attempts}</Td>
                    <Td quiet>{ago(job.queuedAt)}</Td>
                    <Td quiet>{ago(job.endedAt)}</Td>
                    <Td num>
                      {job.status === "failed" ? (
                        <Button onClick={() => retry(job.id)}>Retry</Button>
                      ) : null}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </>
      ) : null}
    </Stack>
  );
}
